import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Eye,
  EyeOff,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Trash2,
  Wrench,
} from 'lucide-react';
import {
  ALLOWED_FILE_TYPES,
  formatCents,
  formatDate,
  maintenanceInputSchema,
  maintenanceKinds,
  maintenancePerformers,
  MAX_FILE_BYTES,
  parseEuroToCents,
  todayIso,
  type ExpenseCategory,
  type MaintenanceEntry,
  type MaintenanceKind,
  type MaintenancePerformer,
} from '@vuokramani/shared';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  Panel,
  SelectField,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { ApiError, errorKey } from '../../lib/api.ts';
import { useDeleteMaintenance, useMaintenance, useSaveMaintenance } from '../../lib/maintenance.ts';
import { useProperty } from '../../lib/properties.ts';
import { useUploadFiles } from '../../lib/tenancy.ts';
import { FileList } from './Files.tsx';
import { ExpenseDialog } from '../landlord/Expenses.tsx';

/** Huoltokirjan laji → kululajin oletus. Remontti voi olla myös perusparannus. */
const EXPENSE_FROM_KIND: Record<MaintenanceEntry['kind'], ExpenseCategory> = {
  renovation: 'repair',
  repair: 'repair',
  purchase: 'furniture',
  housing_company: 'other',
  other: 'other',
};
import ts from './tenancy.module.css';
import s from './maintenance.module.css';

/**
 * Huoltokirja: asunnolle tehdyt remontit, korjaukset, hankinnat ja taloyhtiön työt
 * vuosittain, uusin ensin. Vuokranantaja lisää ja muokkaa; vuokralainen näkee vain
 * hänelle näkyviksi merkityt, ilman hintaa (rajaus tehdään backendissä).
 */
export function MaintenanceLog({
  propertyId,
  editable,
  emptyText,
}: {
  propertyId: string;
  editable: boolean;
  emptyText: string;
}) {
  const { t } = useTranslation();
  const { data: entries } = useMaintenance(propertyId);
  const [editing, setEditing] = useState<MaintenanceEntry | 'new' | null>(null);

  const years = new Map<string, MaintenanceEntry[]>();
  for (const e of entries ?? []) {
    const year = e.happenedOn.slice(0, 4);
    years.set(year, [...(years.get(year) ?? []), e]);
  }

  return (
    <>
      {editable && (
        <div className={ts.toolbar}>
          <p className={ts.fileMeta} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Eye size={14} className={ts.shareOn} aria-hidden="true" />
            {t('maintenance.legend')}
          </p>
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus size={16} aria-hidden="true" />
            {t('maintenance.add')}
          </Button>
        </div>
      )}

      {entries && entries.length === 0 && (
        <EmptyState icon={<Wrench size={22} />} title={t('maintenance.empty')} text={emptyText} />
      )}

      <div className={s.years}>
        {[...years].map(([year, list]) => (
          <section key={year}>
            <h2 className={ts.groupTitle}>
              {year} <span className={ts.groupCount}>{list.length}</span>
            </h2>
            <Panel padded={false}>
              <ul className={s.list}>
                {list.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    propertyId={propertyId}
                    editable={editable}
                    onEdit={() => setEditing(e)}
                  />
                ))}
              </ul>
            </Panel>
          </section>
        ))}
      </div>

      {editing && (
        <EntryDialog
          propertyId={propertyId}
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/** Tapahtuma rivinä; avautuu näyttämään huomiot, takuun ja liitteet. */
function EntryRow({
  entry: e,
  propertyId,
  editable,
  onEdit,
}: {
  entry: MaintenanceEntry;
  propertyId: string;
  editable: boolean;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const remove = useDeleteMaintenance(propertyId);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [expense, setExpense] = useState(false);
  const detailsId = `entry-${e.id}`;

  const meta = [
    t(`maintenanceKinds.${e.kind}`),
    e.performer ? t(`maintenancePerformers.${e.performer}`) : null,
  ].filter(Boolean);

  return (
    <li className={s.entry}>
      <button
        type="button"
        className={s.head}
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen(!open)}
      >
        <span className={s.date}>{formatDate(e.happenedOn, i18n.language)}</span>
        <span className={s.main}>
          <span className={s.title}>{e.title}</span>
          <span className={s.meta}>
            {meta.join(' · ')}
            {e.attachments.length > 0 && (
              <span className={s.clip}>
                <Paperclip size={13} aria-hidden="true" />
                {e.attachments.length}
              </span>
            )}
            {editable && !e.visibleToTenant && (
              <span className={s.clip}>
                <EyeOff size={13} aria-hidden="true" />
                {t('maintenance.hidden')}
              </span>
            )}
          </span>
        </span>
        <span className={s.price}>
          {e.priceCents !== null ? formatCents(e.priceCents, i18n.language) : ''}
        </span>
        <span className={s.eye}>
          {editable && e.visibleToTenant && (
            <Eye
              size={15}
              className={ts.shareOn}
              role="img"
              aria-label={t('property.sharedWithTenant')}
            />
          )}
        </span>
        <ChevronDown
          size={18}
          className={`${s.chevron} ${open ? s.chevronOpen : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div id={detailsId} className={s.details}>
          {e.notes ? (
            <p className={s.notes}>{e.notes}</p>
          ) : (
            <p className={s.muted}>{t('maintenance.noNotes')}</p>
          )}
          {e.warrantyUntil && <Warranty until={e.warrantyUntil} />}
          {e.attachments.length > 0 && (
            <div>
              <h3 className={s.subhead}>{t('maintenance.attachments')}</h3>
              <FileList
                files={e.attachments}
                propertyId={propertyId}
                editable={editable}
                showEntry={false}
              />
            </div>
          )}
          {editable && (
            <div className={s.actions}>
              <Button onClick={onEdit}>
                <Pencil size={15} aria-hidden="true" />
                {t('edit.edit')}
              </Button>
              {e.expenseId ? (
                <span className={ts.fileMeta} style={{ alignSelf: 'center' }}>
                  <Receipt size={14} aria-hidden="true" /> {t('maintenance.expenseDone')}
                </span>
              ) : (
                <Button onClick={() => setExpense(true)}>
                  <Receipt size={15} aria-hidden="true" />
                  {t('maintenance.toExpense')}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setConfirm(true)}>
                <Trash2 size={15} aria-hidden="true" />
                {t('common.delete')}
              </Button>
            </div>
          )}
        </div>
      )}

      {expense && (
        <ExpenseDialog
          propertyId={propertyId}
          expense={null}
          prefill={{
            paidOn: e.happenedOn,
            category: EXPENSE_FROM_KIND[e.kind],
            description: e.title,
            amountCents: e.priceCents,
            maintenanceEntryId: e.id,
            fileId: e.attachments[0]?.id ?? null,
            fileName: e.attachments[0]?.name ?? null,
          }}
          onClose={() => setExpense(false)}
        />
      )}
      <Dialog open={confirm} onClose={() => setConfirm(false)} title={t('maintenance.deleteTitle')}>
        <p>
          {e.attachments.length > 0
            ? t('maintenance.deleteTextFiles', { title: e.title })
            : t('maintenance.deleteText', { title: e.title })}
        </p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setConfirm(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={remove.isPending}
            onClick={() => remove.mutate(e.id, { onSuccess: () => setConfirm(false) })}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </li>
  );
}

function Warranty({ until }: { until: string }) {
  const { t, i18n } = useTranslation();
  const valid = until >= todayIso();
  const date = formatDate(until, i18n.language);
  return (
    <p className={`${s.warranty} ${valid ? s.warrantyValid : ''}`}>
      {valid ? t('maintenance.warrantyValid', { date }) : t('maintenance.warrantyEnded', { date })}
    </p>
  );
}

/** Lisäys ja muokkaus. Uudet liitteet ladataan, kun tapahtuma on tallennettu. */
function EntryDialog({
  propertyId,
  entry,
  onClose,
}: {
  propertyId: string;
  entry: MaintenanceEntry | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const save = useSaveMaintenance(propertyId);
  const upload = useUploadFiles(propertyId);
  const [happenedOn, setHappenedOn] = useState(entry?.happenedOn ?? todayIso());
  const [title, setTitle] = useState(entry?.title ?? '');
  const [kind, setKind] = useState<MaintenanceKind>(entry?.kind ?? 'repair');
  const [performer, setPerformer] = useState<MaintenancePerformer | ''>(entry?.performer ?? '');
  const [price, setPrice] = useState(
    entry?.priceCents != null
      ? (entry.priceCents / 100).toLocaleString(i18n.language, { minimumFractionDigits: 0 })
      : '',
  );
  const [warrantyUntil, setWarrantyUntil] = useState(entry?.warrantyUntil ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  // Uuden merkinnän oletusjako asunnon asetuksesta.
  const { data: property } = useProperty(propertyId);
  const [visible, setVisible] = useState(
    entry?.visibleToTenant ?? property?.settings.maintenanceShared ?? false,
  );
  const [pending, setPending] = useState<File[]>([]);
  // Uusi tapahtuma saa id:n ensimmäisessä tallennuksessa. Jos liitteen lataus epäonnistuu,
  // uusi yritys päivittää saman tapahtuman eikä luo toista.
  const [savedId, setSavedId] = useState(entry?.id);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function choose(list: FileList | null) {
    const files = Array.from(list ?? []);
    const tooBig = files.find((f) => f.size > MAX_FILE_BYTES);
    setErrors((prev) => ({
      ...prev,
      files: tooBig ? t('files.tooLarge', { name: tooBig.name }) : '',
    }));
    setPending((prev) => [...prev, ...files.filter((f) => f.size <= MAX_FILE_BYTES)]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const priceCents = price.trim() === '' ? null : parseEuroToCents(price);
    const next: Record<string, string> = {};
    if (priceCents === null && price.trim() !== '') next.price = t('errors.price');
    if (priceCents !== null && priceCents < 0) next.price = t('errors.price');
    const parsed = maintenanceInputSchema.safeParse({
      happenedOn,
      title,
      kind,
      performer: performer || null,
      priceCents,
      warrantyUntil: warrantyUntil || null,
      notes,
      visibleToTenant: visible,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0]);
        next[field === 'priceCents' ? 'price' : field] ??= t(issue.message);
      }
    }
    if (Object.keys(next).length > 0 || !parsed.success) {
      setErrors(next);
      return;
    }
    setErrors({});
    const saved = await save.mutateAsync({ id: savedId, body: parsed.data }).catch(() => null);
    if (!saved) return;
    setSavedId(saved.id);
    // Kuvat Kuvat-kansioon, muut (kuitit, takuupaperit) Kuitit-kansioon. Kuitit eivät
    // koskaan näy vuokralaiselle automaattisesti; kuvat näkyvät, jos tapahtuma näkyy.
    const images = pending.filter((f) => f.type.startsWith('image/'));
    const docs = pending.filter((f) => !f.type.startsWith('image/'));
    try {
      if (images.length > 0) {
        await upload.mutateAsync({
          files: images,
          folder: 'photo',
          shared: visible,
          maintenanceEntryId: saved.id,
        });
        setPending(docs);
      }
      if (docs.length > 0) {
        await upload.mutateAsync({
          files: docs,
          folder: 'receipt',
          shared: false,
          maintenanceEntryId: saved.id,
        });
      }
    } catch {
      return; // virhe näytetään alla; tapahtuma on jo tallennettu
    }
    onClose();
  }

  const err = (k: string) => errors[k] || undefined;
  const uploadError =
    upload.error instanceof ApiError && upload.error.code === 'file_type'
      ? t('files.wrongType', { name: upload.error.message })
      : upload.isError
        ? t('maintenance.uploadFailed')
        : undefined;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={entry ? t('maintenance.editTitle') : t('maintenance.add')}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <div className={s.grid}>
          <TextField
            label={t('maintenance.date')}
            type="date"
            value={happenedOn}
            onChange={(e) => setHappenedOn(e.target.value)}
            error={err('happenedOn')}
          />
          <SelectField
            label={t('maintenance.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as MaintenanceKind)}
          >
            {maintenanceKinds.map((k) => (
              <option key={k} value={k}>
                {t(`maintenanceKinds.${k}`)}
              </option>
            ))}
          </SelectField>
        </div>
        <TextField
          label={t('maintenance.title')}
          hint={t('maintenance.titleHint')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={err('title')}
        />
        <div className={s.grid}>
          <SelectField
            label={t('maintenance.performer')}
            value={performer}
            onChange={(e) => setPerformer(e.target.value as MaintenancePerformer | '')}
          >
            <option value="">{t('common.notSet')}</option>
            {maintenancePerformers.map((p) => (
              <option key={p} value={p}>
                {t(`maintenancePerformers.${p}`)}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('maintenance.price')}
            hint={t('maintenance.priceHint')}
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            error={err('price')}
          />
        </div>
        <TextField
          label={t('maintenance.warrantyUntil')}
          hint={t('maintenance.warrantyHint')}
          type="date"
          value={warrantyUntil}
          onChange={(e) => setWarrantyUntil(e.target.value)}
          error={err('warrantyUntil')}
        />
        <TextArea
          label={t('maintenance.notes')}
          value={notes}
          rows={3}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div>
          <span className={s.subhead}>{t('maintenance.attachments')}</span>
          {entry && entry.attachments.length > 0 && (
            <FileList
              files={entry.attachments}
              propertyId={propertyId}
              editable
              showEntry={false}
            />
          )}
          {pending.length > 0 && (
            <ul className={ts.selected} style={{ margin: '0.5rem 0' }}>
              {pending.map((f, i) => (
                <li key={`${f.name}-${i}`}>{f.name}</li>
              ))}
            </ul>
          )}
          <label className={`${ts.dropzone} ${s.attach}`}>
            <Paperclip size={18} aria-hidden="true" />
            <span>{t('maintenance.addAttachment')}</span>
            <span className={ts.fileMeta}>{t('maintenance.attachmentHint')}</span>
            <input
              type="file"
              multiple
              accept={ALLOWED_FILE_TYPES.join(',')}
              className="visually-hidden"
              onChange={(e) => {
                choose(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
          {err('files') && <Alert>{err('files')}</Alert>}
        </div>

        <label className={ts.check}>
          <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
          <span>{t('maintenance.visibleToTenant')}</span>
        </label>
        <p className={ts.fileMeta} style={{ marginTop: '-0.6rem' }}>
          {t('maintenance.visibleHint')}
        </p>

        {save.isError && <Alert>{t(errorKey(save.error))}</Alert>}
        {uploadError && <Alert>{uploadError}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending || upload.isPending}>
            {upload.isPending ? t('files.uploading') : t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
